import { useEffect, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { REDIRECT_TX_COOKIE_NAME } from "../../../../constants/env";
import { useIsLogged } from "../../../../hooks/auth/use-is-logged/use-is-logged";
import { getCookie } from "../../../../legacy/utils/getCookie";
import { nextUrlRedirectService } from "../services/next-url-redirect/next-url-redirect.service.instance";
import type { INextUrlData, IPendingRedirect } from "../types/next-url-redirect.type";

interface IUseNextUrlRedirectGuard {
  loading: boolean;
  redirectTo: string | null;
}

function findMatchingRedirect(redirects: IPendingRedirect[], path: string): IPendingRedirect | undefined {
  return redirects.find(function checkMatch(redirect) {
    return matchPath(redirect.triggerRoute, path) !== null;
  });
}

function buildRedirectUrl(nextUrl: INextUrlData): string {
  if (nextUrl.isExternal) {
    return `${nextUrl.origin}${nextUrl.pathname}${nextUrl.search}`;
  }
  return `${nextUrl.pathname}${nextUrl.search}`;
}

function isRedirectingInTheSamePath(nextUrl: INextUrlData, currentPathname: string): boolean {
  if (nextUrl.isExternal) {
    return false;
  }
  return nextUrl.pathname === currentPathname;
}

function hasPendingRedirectCookie(): boolean {
  return Boolean(getCookie(REDIRECT_TX_COOKIE_NAME));
}

export function useNextUrlRedirectGuard(): IUseNextUrlRedirectGuard {
  const isLogged = useIsLogged();
  const location = useLocation();
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => isLogged && hasPendingRedirectCookie());
  const isMountedRef = useRef(true);

  useEffect(
    function runNextUrlRedirectGuard() {
      isMountedRef.current = true;
      setRedirectTo(null);

      if (!isLogged) {
        setLoading(false);
        return;
      }

      if (!hasPendingRedirectCookie()) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const controller = new AbortController();

      function isActive(): boolean {
        return isMountedRef.current && !controller.signal.aborted;
      }

      async function checkRedirect(): Promise<void> {
        const pendingRedirects = await nextUrlRedirectService.getPendingRedirects();
        if (!isActive()) {
          return;
        }
        if (!pendingRedirects) {
          setLoading(false);
          return;
        }

        const matchingRedirect = findMatchingRedirect(pendingRedirects, location.pathname);
        if (!matchingRedirect) {
          setLoading(false);
          return;
        }

        await nextUrlRedirectService.clearPendingRedirect(matchingRedirect.triggerRoute);

        if (!isActive()) {
          return;
        }

        if (isRedirectingInTheSamePath(matchingRedirect.nextURL, location.pathname)) {
          setLoading(false);
          return;
        }

        const redirectUrl = buildRedirectUrl(matchingRedirect.nextURL);
        if (matchingRedirect.nextURL.isExternal) {
          window.location.href = redirectUrl;
          return;
        }

        setRedirectTo(redirectUrl);
        setLoading(false);
      }

      checkRedirect().catch(function onError() {
        if (isActive()) {
          setLoading(false);
        }
      });

      return function cancelGuardCheck() {
        isMountedRef.current = false;
        controller.abort();
      };
    },
    [isLogged, location.pathname]
  );

  return { loading, redirectTo };
}
